import * as React from "react"
import { cn } from "@/lib/utils"

type ResponsiveValue<T> = T | { xs?: T; sm?: T; md?: T; lg?: T; xl?: T }

const resolveResponsiveValue = <T,>(value: ResponsiveValue<T> | undefined): T | undefined => {
    if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        ("xs" in value || "sm" in value || "md" in value || "lg" in value || "xl" in value)
    ) {
        return value.xs ?? value.sm ?? value.md ?? value.lg ?? value.xl
    }

    return value as T | undefined
}

interface BoxProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode
    component?: keyof JSX.IntrinsicElements
    sx?: React.CSSProperties
    display?: ResponsiveValue<React.CSSProperties["display"]>
    flexDirection?: ResponsiveValue<React.CSSProperties["flexDirection"]>
    justifyContent?: ResponsiveValue<React.CSSProperties["justifyContent"]>
    alignItems?: ResponsiveValue<React.CSSProperties["alignItems"]>
    alignContent?: ResponsiveValue<React.CSSProperties["alignContent"]>
    flexWrap?: ResponsiveValue<React.CSSProperties["flexWrap"]>
    gap?: ResponsiveValue<React.CSSProperties["gap"]>
    flex?: ResponsiveValue<React.CSSProperties["flex"]>
    flexGrow?: ResponsiveValue<React.CSSProperties["flexGrow"]>
    flexShrink?: ResponsiveValue<React.CSSProperties["flexShrink"]>
    width?: ResponsiveValue<React.CSSProperties["width"]>
    height?: ResponsiveValue<React.CSSProperties["height"]>
    minWidth?: ResponsiveValue<React.CSSProperties["minWidth"]>
    minHeight?: ResponsiveValue<React.CSSProperties["minHeight"]>
    maxWidth?: ResponsiveValue<React.CSSProperties["maxWidth"]>
    maxHeight?: ResponsiveValue<React.CSSProperties["maxHeight"]>
    position?: ResponsiveValue<React.CSSProperties["position"]>
    top?: ResponsiveValue<React.CSSProperties["top"]>
    right?: ResponsiveValue<React.CSSProperties["right"]>
    bottom?: ResponsiveValue<React.CSSProperties["bottom"]>
    left?: ResponsiveValue<React.CSSProperties["left"]>
    overflow?: ResponsiveValue<React.CSSProperties["overflow"]>
    overflowX?: ResponsiveValue<React.CSSProperties["overflowX"]>
    overflowY?: ResponsiveValue<React.CSSProperties["overflowY"]>
    textAlign?: ResponsiveValue<React.CSSProperties["textAlign"]>
    bgcolor?: ResponsiveValue<React.CSSProperties["backgroundColor"]>
    backgroundColor?: ResponsiveValue<React.CSSProperties["backgroundColor"]>
    border?: ResponsiveValue<React.CSSProperties["border"]>
    borderRadius?: ResponsiveValue<React.CSSProperties["borderRadius"]>
    boxShadow?: ResponsiveValue<React.CSSProperties["boxShadow"]>
    p?: ResponsiveValue<React.CSSProperties["padding"]>
    px?: ResponsiveValue<React.CSSProperties["paddingInline"]>
    py?: ResponsiveValue<React.CSSProperties["paddingBlock"]>
    pt?: ResponsiveValue<React.CSSProperties["paddingTop"]>
    pr?: ResponsiveValue<React.CSSProperties["paddingRight"]>
    pb?: ResponsiveValue<React.CSSProperties["paddingBottom"]>
    pl?: ResponsiveValue<React.CSSProperties["paddingLeft"]>
    m?: ResponsiveValue<React.CSSProperties["margin"]>
    mx?: ResponsiveValue<React.CSSProperties["marginInline"]>
    my?: ResponsiveValue<React.CSSProperties["marginBlock"]>
    mt?: ResponsiveValue<React.CSSProperties["marginTop"]>
    mr?: ResponsiveValue<React.CSSProperties["marginRight"]>
    mb?: ResponsiveValue<React.CSSProperties["marginBottom"]>
    ml?: ResponsiveValue<React.CSSProperties["marginLeft"]>
}

const Box = React.forwardRef<HTMLDivElement, BoxProps>(
    ({
        className,
        children,
        component,
        sx,
        style,
        display,
        flexDirection,
        justifyContent,
        alignItems,
        alignContent,
        flexWrap,
        gap,
        flex,
        flexGrow,
        flexShrink,
        width,
        height,
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        position,
        top,
        right,
        bottom,
        left,
        overflow,
        overflowX,
        overflowY,
        textAlign,
        bgcolor,
        backgroundColor,
        border,
        borderRadius,
        boxShadow,
        p,
        px,
        py,
        pt,
        pr,
        pb,
        pl,
        m,
        mx,
        my,
        mt,
        mr,
        mb,
        ml,
        ...props
    }, ref) => {
        const Comp = (component ?? "div") as keyof JSX.IntrinsicElements
        const resolvedStyle: React.CSSProperties = {
            ...sx,
            ...style,
            display: resolveResponsiveValue(display),
            flexDirection: resolveResponsiveValue(flexDirection),
            justifyContent: resolveResponsiveValue(justifyContent),
            alignItems: resolveResponsiveValue(alignItems),
            alignContent: resolveResponsiveValue(alignContent),
            flexWrap: resolveResponsiveValue(flexWrap),
            gap: resolveResponsiveValue(gap),
            flex: resolveResponsiveValue(flex),
            flexGrow: resolveResponsiveValue(flexGrow),
            flexShrink: resolveResponsiveValue(flexShrink),
            width: resolveResponsiveValue(width),
            height: resolveResponsiveValue(height),
            minWidth: resolveResponsiveValue(minWidth),
            minHeight: resolveResponsiveValue(minHeight),
            maxWidth: resolveResponsiveValue(maxWidth),
            maxHeight: resolveResponsiveValue(maxHeight),
            position: resolveResponsiveValue(position),
            top: resolveResponsiveValue(top),
            right: resolveResponsiveValue(right),
            bottom: resolveResponsiveValue(bottom),
            left: resolveResponsiveValue(left),
            overflow: resolveResponsiveValue(overflow),
            overflowX: resolveResponsiveValue(overflowX),
            overflowY: resolveResponsiveValue(overflowY),
            textAlign: resolveResponsiveValue(textAlign),
            backgroundColor: resolveResponsiveValue(backgroundColor) ?? resolveResponsiveValue(bgcolor),
            border: resolveResponsiveValue(border),
            borderRadius: resolveResponsiveValue(borderRadius),
            boxShadow: resolveResponsiveValue(boxShadow),
            padding: resolveResponsiveValue(p),
            paddingInline: resolveResponsiveValue(px),
            paddingBlock: resolveResponsiveValue(py),
            paddingTop: resolveResponsiveValue(pt),
            paddingRight: resolveResponsiveValue(pr),
            paddingBottom: resolveResponsiveValue(pb),
            paddingLeft: resolveResponsiveValue(pl),
            margin: resolveResponsiveValue(m),
            marginInline: resolveResponsiveValue(mx),
            marginBlock: resolveResponsiveValue(my),
            marginTop: resolveResponsiveValue(mt),
            marginRight: resolveResponsiveValue(mr),
            marginBottom: resolveResponsiveValue(mb),
            marginLeft: resolveResponsiveValue(ml),
        }

        return (
            <Comp
                ref={ref}
                className={cn(
                    "rounded-lg border bg-card text-card-foreground shadow-sm p-6",
                    className
                )}
                style={resolvedStyle}
                {...props}
            >
                {children}
            </Comp>
        )
    }
)
Box.displayName = "Box"

export { Box }
